import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import Navbar from './components/Navbar';
import ToastViewport from './components/ToastViewport';
import ProtectedRoute from './components/ProtectedRoute';
import OperatingContextProvider from './components/OperatingContextProvider';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import SalaryPage from './pages/SalaryPage';
import SchedulePage from './pages/SchedulePage';
import MenuPage from './pages/MenuPage';
import EventVenueBookingPage from './pages/EventVenueBookingPage';
import AdminDashboard from './pages/AdminDashboard';
import GuestProfilePage from './pages/GuestProfilePage';
import OrderPage from './pages/OrderPage';
import PaymentPage from './pages/PaymentPage';
import ProductsPage from './pages/management/ProductsPage';
import AllergensPage from './pages/management/AllergensPage';
import ProductCategoriesPage from './pages/management/ProductCategoriesPage';
import SuppliersPage from './pages/management/SuppliersPage';
import SemiFinishedPage from './pages/management/SemiFinishedPage';
import SemiFinishedProductionPage from './pages/management/SemiFinishedProductionPage';
import KitchenWorkPage from './pages/management/KitchenWorkPage';
import KitchenExecutionPage from './pages/management/KitchenExecutionPage';
import DishesPage from './pages/management/DishesPage';
import SaleCatalogPage from './pages/management/SaleCatalogPage';
import InventoryPage from './pages/management/InventoryPage';
import EventOrdersPage from './pages/management/EventOrdersPage';
import EventVenuesPage from './pages/management/EventVenuesPage';
import EventVenueBookingsPage from './pages/management/EventVenueBookingsPage';
import VenueLayoutPage from './pages/VenueLayoutPage';
import AdditionalServicesPage from './pages/management/AdditionalServicesPage';
import InventoryReceiptsPage from './pages/management/InventoryReceiptsPage';
import InventoryBalancePage from './pages/management/InventoryBalancePage';
import InventoryRevisionsPage from './pages/management/InventoryRevisionsPage';
import InventoryTransfersPage from './pages/management/InventoryTransfersPage';
import InventoryWarehouseTransfersPage from './pages/management/InventoryWarehouseTransfersPage';
import InventoryWriteOffOpsPage from './pages/management/InventoryWriteOffOpsPage';
import InventoryNutritionOpsPage from './pages/management/InventoryNutritionOpsPage';
import NutritionBasePage from './pages/management/NutritionBasePage';
import InventoryResortOpsPage from './pages/management/InventoryResortOpsPage';
import KitchenEmployeesPage from './pages/management/KitchenEmployeesPage';
import HallsPayrollPage from './pages/management/HallsPayrollPage';
import SalaryPayrollPage from './pages/management/SalaryPayrollPage';
import RestaurantPayrollPage from './pages/management/RestaurantPayrollPage';
import PurchaseWorksheetPage from './pages/management/PurchaseWorksheetPage';
import SupplierPurchaseOrdersPage from './pages/management/SupplierPurchaseOrdersPage';
import ProcurementFinancialDashboardPage from './pages/management/ProcurementFinancialDashboardPage';
import SupplierPaymentBatchesPage from './pages/management/SupplierPaymentBatchesPage';
import AccountingPeriodsPage from './pages/management/AccountingPeriodsPage';
import PreparationWorksheetsPage from './pages/management/PreparationWorksheetsPage';
import PreparationOperationsPage from './pages/management/PreparationOperationsPage';
import VenueAnalyticsPage from './pages/management/VenueAnalyticsPage';
import EventFinancialAnalyticsPage from './pages/management/EventFinancialAnalyticsPage';
import KitchenwarePage from './pages/management/KitchenwarePage';
import PurchasePlanningPage from './pages/management/PurchasePlanningPage';
import OrderSupplyPage from './pages/management/OrderSupplyPage';
import InventoryActivityFeedPage from './pages/management/InventoryActivityFeedPage';
import AuditLogsPage from './pages/management/AuditLogsPage';
import CrmCustomersPage from './pages/management/CrmCustomersPage';
import CrmDashboardPage from './pages/management/CrmDashboardPage';
import HotelPage from './pages/management/HotelPage';
import WorkspacePage from './pages/WorkspacePage';
import OperatingContextsPage from './pages/OperatingContextsPage';
import ExportImportPage from './pages/ExportImportPage';
import EmployeesPage from './pages/EmployeesPage';
import { MANAGEMENT_ROLES, WORKER_AND_MANAGEMENT_ROLES } from './constants/management';
import { getPagePermission } from './constants/permissions';
import { applyUiTheme, isUiTheme, UI_THEME_STORAGE_KEY, UiTheme } from './constants/uiTheme';

const App: React.FC = () => {
  useEffect(() => {
    const savedTheme = localStorage.getItem(UI_THEME_STORAGE_KEY);
    const theme: UiTheme = savedTheme && isUiTheme(savedTheme) ? savedTheme : 'light';
    applyUiTheme(theme);
  }, []);

  useEffect(() => {
    const wheelSensitiveInputTypes = new Set(['number', 'date', 'time', 'datetime-local', 'month', 'week', 'range']);
    const handleWheel = (event: WheelEvent) => {
      const activeElement = document.activeElement;
      if (!(activeElement instanceof HTMLInputElement)) return;
      if (!wheelSensitiveInputTypes.has(activeElement.type)) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (!activeElement.contains(target)) return;
      activeElement.blur();
    };

    document.addEventListener('wheel', handleWheel, true);
    return () => {
      document.removeEventListener('wheel', handleWheel, true);
    };
  }, []);

  return (
    <Provider store={store}>
      <BrowserRouter>
        <OperatingContextProvider>
        <Navbar />
        <ToastViewport />
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/operating-contexts" element={<ProtectedRoute contextIndependent><OperatingContextsPage /></ProtectedRoute>} />
        <Route
          path="/workspace"
          element={
            <ProtectedRoute>
              <WorkspacePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salary"
          element={
            <ProtectedRoute>
              <SalaryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/schedule"
          element={
            <ProtectedRoute>
              <SchedulePage />
            </ProtectedRoute>
          }
        />
        <Route path="/menu" element={<MenuPage />} />
        <Route path="/book-event" element={<EventVenueBookingPage />} />
        <Route path="/booking-layout" element={<VenueLayoutPage customer />} />
        <Route path="/management/venue-layouts" element={
          <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/event-venues')}>
            <VenueLayoutPage />
          </ProtectedRoute>
        } />
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/admin')}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/employees"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/admin/employees')}>
              <EmployeesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/products"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/products')}>
              <ProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/allergens"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/allergens')}>
              <AllergensPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/product-categories"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/product-categories')}>
              <ProductCategoriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/suppliers"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/suppliers')}>
              <SuppliersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/semi-finished"
          element={
            <ProtectedRoute roles={WORKER_AND_MANAGEMENT_ROLES} permission={getPagePermission('/management/semi-finished')}>
              <SemiFinishedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/kitchen-execution"
          element={<ProtectedRoute><KitchenExecutionPage /></ProtectedRoute>}
        />
        <Route
          path="/management/kitchen-work"
          element={
            <ProtectedRoute>
              <KitchenWorkPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/semi-finished-production"
          element={
            <ProtectedRoute roles={WORKER_AND_MANAGEMENT_ROLES} permission={getPagePermission('/management/semi-finished-production')}>
              <SemiFinishedProductionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/dishes"
          element={
            <ProtectedRoute roles={WORKER_AND_MANAGEMENT_ROLES} permission={getPagePermission('/management/dishes')}>
              <DishesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/sale-catalog"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/sale-catalog')}>
              <SaleCatalogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/nutrition-base"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/nutrition-base')}>
              <NutritionBasePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/inventory"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/inventory')}>
              <InventoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/event-venues"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/event-venues')}>
              <EventVenuesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/venue-bookings"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/venue-bookings')}>
              <EventVenueBookingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/additional-services"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/additional-services')}>
              <AdditionalServicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/event-orders"
          element={
            <ProtectedRoute roles={WORKER_AND_MANAGEMENT_ROLES} permission={getPagePermission('/management/event-orders')}>
              <EventOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/hotel"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/hotel')}>
              <HotelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/crm-dashboard"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/crm-dashboard')}>
              <CrmDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/customers"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/customers')}>
              <CrmCustomersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/inventory-receipts"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/inventory-receipts')}>
              <InventoryReceiptsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/inventory-balance"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/inventory-balance')}>
              <InventoryBalancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/inventory-transfers"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/inventory-transfers')}>
              <InventoryTransfersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/inventory-warehouse-transfers"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/inventory-transfers')}>
              <InventoryWarehouseTransfersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/inventory-write-offs"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/inventory-write-offs')}>
              <InventoryWriteOffOpsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/inventory-nutrition"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/inventory-nutrition')}>
              <InventoryNutritionOpsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/inventory-resort"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/inventory-resort')}>
              <InventoryResortOpsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/employees"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/employees')}>
              <KitchenEmployeesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/halls-payroll"
          element={
            <ProtectedRoute permission={getPagePermission('/management/halls-payroll')}>
              <HallsPayrollPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/salary-payroll"
          element={
            <ProtectedRoute permission={getPagePermission('/management/salary-payroll')}>
              <SalaryPayrollPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/restaurant-payroll"
          element={
            <ProtectedRoute permission={getPagePermission('/management/restaurant-payroll')}>
              <RestaurantPayrollPage />
            </ProtectedRoute>
          }
        />
        <Route path="/management/kitchen-employees" element={<Navigate to="/management/employees" replace />} />
        <Route
          path="/management/inventory-revisions"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/inventory-revisions')}>
              <InventoryRevisionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/purchase-worksheet"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/purchase-worksheet')}>
              <PurchaseWorksheetPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/purchase-planning"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES}>
              <PurchasePlanningPage />
            </ProtectedRoute>
          }
        />
        <Route path="/management/order-supply" element={
          <ProtectedRoute permission={getPagePermission('/management/order-supply')}>
            <OrderSupplyPage />
          </ProtectedRoute>
        } />
        <Route
          path="/management/supplier-purchase-orders"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/supplier-purchase-orders')}>
              <SupplierPurchaseOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/procurement-finance"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/analytics/procurement-finance')}>
              <ProcurementFinancialDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/supplier-payment-batches"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/supplier-payment-batches')}>
              <SupplierPaymentBatchesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/accounting-periods"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/accounting-periods')}>
              <AccountingPeriodsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/inventory-activity"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/inventory-activity')}>
              <InventoryActivityFeedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <ProtectedRoute permission={getPagePermission('/admin/audit-logs')}>
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/preparation-worksheets"
          element={
            <ProtectedRoute roles={WORKER_AND_MANAGEMENT_ROLES} permission={getPagePermission('/management/preparation-worksheets')}>
              <PreparationWorksheetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/preparation-operations"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/preparation-operations')}>
              <PreparationOperationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/export-import"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/export-import')}>
              <ExportImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/venues"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/analytics/venues')}>
              <VenueAnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/event-finance"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/analytics/event-finance')}>
              <EventFinancialAnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/management/kitchenware"
          element={
            <ProtectedRoute roles={MANAGEMENT_ROLES} permission={getPagePermission('/management/kitchenware')}>
              <KitchenwarePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guests/:id"
          element={
            <ProtectedRoute permission={getPagePermission('/management/event-orders')}>
              <GuestProfilePage />
            </ProtectedRoute>
          }
        />
        <Route path="/order" element={<OrderPage />} />
        <Route path="/order/:locationCode" element={<OrderPage />} />
        <Route path="/payment/:orderId" element={<PaymentPage />} />
        <Route
          path="/unauthorized"
          element={
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <h2>Доступ заборонено</h2>
            </div>
          }
        />
        <Route path="/" element={<Navigate to="/workspace" replace />} />
        </Routes>
        </OperatingContextProvider>
      </BrowserRouter>
    </Provider>
  );
};

export default App;
